import React, { useEffect, useState } from 'react';
import { Box, Grid, TextField } from '@mui/material';
import { styled } from '@mui/material/styles';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutline';
import IconButton from '@mui/material/IconButton';
import type { Image, ThumbnailProps } from '../media-props';

interface Props extends ThumbnailProps {
  caption: string,
  image: Image,
  onCaptionChange?: (newCaption: string) => void,
  editable?: boolean,
  disabled?: boolean,
  selectMedia: (image: Image, caption?: string) => void,
}

const MediaListRow: React.FC<Props> = ({
  url,
  image,
  caption,
  mediaID,
  allowDeleting = false,
  editable = false,
  disabled = false,
  altText = '',
  onDelete = () => {},
  onCaptionChange = () => {},
  selectMedia,
  sx = {},
}) => {
  const emptyLabel = editable && !disabled ? 'Click to add a caption' : 'No caption';
  const [intCaption, setIntCaption] = useState(caption || '');

  const determineLabelText = () => (intCaption.length > 0 ? 'Caption' : emptyLabel);
  const [labelText, setLabelText] = useState(determineLabelText());
  const blurCaption = () => {
    setLabelText(determineLabelText());
  };

  useEffect(() => {
    if (caption) {
      setIntCaption(caption);
    }
  }, [caption]);

  const ThumbImg = styled(Box)`
    width: 100px;
    height: 100px;
    border-radius: 4px;
    object-fit: cover;
    cursor: pointer;
  `;

  return (
    <Grid container spacing={2} alignItems="center" sx={sx}>
      <Grid item xs={3} sx={{ paddingTop: '8px !important' }}>
        <ThumbImg
          component="img"
          // @ts-ignore
          src={url}
          srcSet={url}
          loading="lazy"
          alt={altText}
          onClick={(e) => {
            e.preventDefault();
            selectMedia(image, intCaption);
          }}
        />
      </Grid>
      <Grid
        item
        xs={7}
        sx={{
          '& .MuiTextField-root > .MuiFormLabel-root.MuiFormLabel-filled': { display: 'none' },
          '& .MuiOutlinedInput-notchedOutline': { borderStyle: 'none' },
          '& .MuiTextField-root:hover .Mui-disabled .MuiOutlinedInput-notchedOutline': { borderStyle: 'none' },
          '& .MuiTextField-root:hover > .Mui-disabled.MuiFormLabel-root.MuiFormLabel-filled': { display: 'none' },
          '& .MuiTextField-root:hover .MuiOutlinedInput-notchedOutline': { borderStyle: 'solid', borderColor: 'rgba(0, 0, 0, 0.23)' },
          '& .MuiTextField-root:hover .MuiFormLabel-root': { display: 'block' },
          '& .MuiTextField-root .MuiFormLabel-root.Mui-focused': { display: 'block' },
          '& .MuiInputBase-root.Mui-focused > .MuiOutlinedInput-notchedOutline': { borderStyle: 'solid', borderWidth: '1px', borderColor: 'rgba(0, 0, 0, 0.75)' },
        }}
      >
        <TextField
          placeholder="Enter a caption"
          id={`${mediaID}-caption`}
          label={labelText}
          name="caption"
          variant="outlined"
          maxRows={3}
          fullWidth
          multiline
          value={intCaption}
          onChange={(event) => {
            setIntCaption(event.target.value);
            if (onCaptionChange) {
              onCaptionChange(event.target.value);
            }
          }}
          onFocus={() => { setLabelText('Caption'); }}
          onBlur={blurCaption}
          disabled={disabled || !editable}
          sx={{
            '& textarea': {
              scrollbarWidth: 'none',
            },
            '& textarea:hover, & textarea:focus': {
              scrollbarWidth: 'thin',
            },
          }}
        />
      </Grid>
      <Grid item xs={2} alignItems="flex-end">
        {allowDeleting && (
          <IconButton
            sx={{ borderRadius: '4px', ml: '30px' }}
            size="small"
            title="Delete this file"
            onClick={() => {
              if (onDelete) {
                onDelete(mediaID || '');
              }
            }}
          >
            <DeleteOutlinedIcon />
          </IconButton>
        )}
      </Grid>
    </Grid>
  );
};

export default MediaListRow;
